import React, { useEffect, useState } from "react";
import { Dialog, Box, Typography, IconButton, Button } from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ClockAlert } from "lucide-react";
import CloseIcon from "@mui/icons-material/Close";

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const SubscriptionExpiryDialog = () => {
  const [user, setUser] = useState(null);
  const [userSubscription, setUserSubscription] = useState(null);
  const [open, setOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const navigate = useNavigate();

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem("token");
          navigate("/");
        } else {
          console.error("Profile fetch error", error);
        }
      }
    };

    fetchProfile();
  }, [navigate]);

  // Fetch subscription data
  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const response = await axios.get("https://admin.bizstor.lk/public/api/get-subscription");
        const subsForUser = response.data.subscription
          .filter((sub) => sub.email === user.email)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        const latest = subsForUser[0] || null;
        setUserSubscription(latest);
      } catch (error) {
        console.error("Failed to fetch subscriptions", error);
      }
    };

    if (user?.email) {
      fetchSubscriptions();
    }
  }, [user]);

  // Check expiry logic
  useEffect(() => {
    if (!userSubscription?.end_date) return;

    const today = startOfDay(new Date());
    const endDate = startOfDay(new Date(userSubscription.end_date));
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (endDate.getTime() === tomorrow.getTime()) {
      setStatusMessage("Your subscription will expire tomorrow. Please renew your package to continue using the service.");
      setOpen(true);
    } else if (endDate.getTime() === today.getTime()) {
      setStatusMessage("Your subscription expires today. Please renew your package to avoid interruption.");
      setOpen(true);
    } else if (endDate.getTime() < today.getTime()) {
      setStatusMessage("Your subscription has expired. Please renew your package to continue using the service.");
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [userSubscription]);

  // Redirect to upgrade
  const handleUpgrade = () => {
    setOpen(false);
    navigate("/upgrade");
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <Box
        sx={{
          position: "relative",
          px: 4,
          py: 4,
          minWidth: 350,
          textAlign: "center",
          borderRadius: 3,
          backgroundColor: "#fff",
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={() => setOpen(false)}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            "&:hover": { color: "red" },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Icon */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <ClockAlert size={48} color="#f59e0b" />
        </Box>

        {/* Title */}
        <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
          {statusMessage.includes("tomorrow")
            ? "Subscription Expiring Soon"
            : statusMessage.includes("today")
            ? "Subscription Expires Today"
            : statusMessage.includes("expired")
            ? "Subscription Expired"
            : "Subscription Notice"}
        </Typography>

        {/* Message */}
        <Typography sx={{ mb: 3, color: "text.secondary" }}>
          {statusMessage}
        </Typography>

        {/* Gradient Upgrade Button */}
        <Button
          onClick={handleUpgrade}
          variant="contained"
          sx={{
            fontWeight: "bold",
            textTransform: "none",
            px: 4,
            py: 1.2,
            borderRadius: 2,
            background: "linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)",
            color: "#fff",
            boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
            "&:hover": {
              background: "linear-gradient(90deg, #feb47b 0%, #ff7e5f 100%)",
            },
          }}
        >
          Upgrade Now
        </Button>
      </Box>
    </Dialog>
  );
};

export default SubscriptionExpiryDialog;
